import React, { useEffect, useState } from "react";
import { Link, Route, Routes, NavLink, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, Users, BookOpen, Target, Settings, Globe, Sparkles, Loader2 } from "lucide-react";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = [
    { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/lessons", label: "Lessons", icon: BookOpen },
    { to: "/admin/scenarios", label: "Scenarios", icon: Target },
    { to: "/admin/seo", label: "SEO Pages", icon: Globe },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ];
  return (
    <div className="min-h-screen bg-slate-100 grid md:grid-cols-[240px_1fr]">
      <aside className="bg-slate-900 text-slate-200 p-6 min-h-screen">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center font-display font-extrabold text-white">R</div>
          <div className="font-display font-extrabold text-white">Admin</div>
        </div>
        <nav className="space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({isActive}) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-emerald-700 text-white" : "hover:bg-slate-800"}`}>
              <l.icon size={16}/>{l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8 pt-6 border-t border-slate-700 text-xs">
          <div className="text-slate-400">Signed in as</div>
          <div className="mt-1">{user?.email}</div>
          <div className="mt-4 flex gap-2">
            <Link to="/dashboard" className="text-slate-300 underline">User view</Link>
            <button onClick={async () => { await logout(); nav("/"); }} className="ml-auto flex items-center gap-1 text-slate-300 hover:text-white" data-testid="admin-logout-btn"><LogOut size={12}/>Log out</button>
          </div>
        </div>
      </aside>
      <main className="p-6 sm:p-10">
        <Routes>
          <Route index element={<Overview/>}/>
          <Route path="users" element={<UsersView/>}/>
          <Route path="lessons" element={<ContentEditor kind="lessons"/>}/>
          <Route path="scenarios" element={<ContentEditor kind="scenarios"/>}/>
          <Route path="seo" element={<SeoEditor/>}/>
          <Route path="settings" element={<SettingsView/>}/>
        </Routes>
      </main>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({data}) => setS(data)); }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;
  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl text-slate-900">Admin Overview</h1>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Users" value={s.users_count} testId="admin-stat-users"/>
        <Stat label="Lessons" value={s.lessons_count} testId="admin-stat-lessons"/>
        <Stat label="Scenarios" value={s.scenarios_count} testId="admin-stat-scenarios"/>
        <Stat label="Missions completed" value={s.completed_missions} testId="admin-stat-missions"/>
        <Stat label="Signups (7d)" value={s.signups_7d} testId="admin-stat-signups"/>
      </div>
    </div>
  );
}

function UsersView() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(({data}) => setUsers(data.users));
  useEffect(() => { load(); }, []);
  const setPlan = async (id, plan) => { await api.post(`/admin/users/${id}/plan?plan=${plan}`); toast.success(`Plan set to ${plan}`); load(); };
  return (
    <div>
      <h1 className="font-display font-extrabold text-3xl text-slate-900">Users</h1>
      <div className="mt-8 rle-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left"><tr>
            <th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Role</th><th className="p-3">Plan</th><th className="p-3">Country</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-user-row-${u.id}`}>
              <td className="p-3 font-mono text-xs">{u.email}</td>
              <td className="p-3">{u.name || "—"}</td>
              <td className="p-3">{u.role}</td>
              <td className="p-3 capitalize">{u.plan}</td>
              <td className="p-3">{u.country || "—"}</td>
              <td className="p-3">
                <select onChange={(e) => setPlan(u.id, e.target.value)} value={u.plan} className="border border-slate-200 rounded-lg px-2 py-1 text-xs" data-testid={`admin-user-plan-${u.id}`}>
                  <option value="explore">explore</option><option value="essential">essential</option><option value="complete">complete</option>
                </select>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function ContentEditor({ kind }) {
  const [items, setItems] = useState([]); const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/content/${kind}`).then(({data}) => setItems(data.items));
  useEffect(() => { api.get(`/admin/content/${kind}`).then(({data}) => setItems(data.items)); }, [kind]);
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/content/${kind}/${id}`); toast.success("Deleted"); load(); };
  const save = async (item) => {
    let payload = { ...item };
    if (kind === "lessons" && typeof payload.sections === "string") { try { payload.sections = JSON.parse(payload.sections); } catch { toast.error("Invalid JSON in sections"); return; } }
    if (kind === "scenarios") {
      for (const field of ["target_vocabulary","recommended_phrases","common_mistakes","success_criteria","scripted_replies"]) {
        if (typeof payload[field] === "string") { try { payload[field] = JSON.parse(payload[field]); } catch { toast.error(`Invalid JSON in ${field}`); return; } }
      }
    }
    await api.post(`/admin/content/${kind}`, payload);
    toast.success("Saved"); setEditing(null); load();
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-3xl text-slate-900 capitalize">{kind}</h1>
        <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" onClick={() => setEditing({ status: "draft" })} data-testid={`admin-${kind}-new-btn`}>+ New</Button>
      </div>
      <div className="mt-6 rle-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left"><tr>
            <th className="p-3">Title</th><th className="p-3">Country</th><th className="p-3">Status</th><th className="p-3">Premium</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>{items.map((it) => (
            <tr key={it.id} className="border-t border-slate-100" data-testid={`admin-${kind}-row-${it.id}`}>
              <td className="p-3">{it.title}</td><td className="p-3">{it.country}</td>
              <td className="p-3">{it.status}</td><td className="p-3">{it.premium ? "Yes" : "No"}</td>
              <td className="p-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`admin-${kind}-edit-${it.id}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`admin-${kind}-delete-${it.id}`}>Delete</Button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      {editing && <EditModal kind={kind} item={editing} onClose={() => setEditing(null)} onSave={save}/>}
    </div>
  );
}

function EditModal({ kind, item, onClose, onSave }) {
  const jsonFields = kind === "lessons"
    ? ["sections"]
    : ["target_vocabulary","recommended_phrases","common_mistakes","success_criteria","scripted_replies"];
  const [f, setF] = useState(() => {
    const copy = { ...item };
    for (const jf of jsonFields) if (copy[jf] && typeof copy[jf] !== "string") copy[jf] = JSON.stringify(copy[jf], null, 2);
    return copy;
  });
  const set = (k, v) => setF({...f, [k]: v});
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-edit-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <h2 className="font-display font-bold text-xl text-slate-900">{item.id ? "Edit" : "Create"} {kind}</h2>
        <div className="mt-4 space-y-3">
          <Field label="Title"><Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="admin-edit-title"/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Country (AU/CA/UK)"><Input value={f.country || ""} onChange={(e) => set("country", e.target.value.toUpperCase())} data-testid="admin-edit-country"/></Field>
            <Field label="Status"><select value={f.status || "draft"} onChange={(e) => set("status", e.target.value)} className="border rounded-lg px-2 py-2 w-full" data-testid="admin-edit-status">
              <option value="draft">draft</option><option value="under_review">under_review</option><option value="published">published</option><option value="archived">archived</option>
            </select></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Level"><Input value={f.level || ""} onChange={(e) => set("level", e.target.value)}/></Field>
            <Field label="Goal"><Input value={f.goal || ""} onChange={(e) => set("goal", e.target.value)}/></Field>
            <Field label="Order"><Input type="number" value={f.order || 0} onChange={(e) => set("order", Number(e.target.value))}/></Field>
          </div>
          <Field label="Premium"><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.premium} onChange={(e) => set("premium", e.target.checked)} data-testid="admin-edit-premium"/>Premium-only</label></Field>
          {kind === "lessons" && <Field label="Summary"><Textarea value={f.summary || ""} onChange={(e) => set("summary", e.target.value)}/></Field>}
          {kind === "scenarios" && (
            <>
              <Field label="Context"><Textarea value={f.context || ""} onChange={(e) => set("context", e.target.value)}/></Field>
              <Field label="Objective"><Textarea value={f.objective || ""} onChange={(e) => set("objective", e.target.value)}/></Field>
              <Field label="Opening message"><Textarea value={f.opening_message || ""} onChange={(e) => set("opening_message", e.target.value)}/></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Learner role"><Input value={f.learner_role || ""} onChange={(e) => set("learner_role", e.target.value)}/></Field>
                <Field label="AI role"><Input value={f.ai_role || ""} onChange={(e) => set("ai_role", e.target.value)}/></Field>
              </div>
            </>
          )}
          {jsonFields.map((jf) => (
            <Field key={jf} label={`${jf} (JSON)`}><Textarea rows={6} className="font-mono text-xs" value={f[jf] || ""} onChange={(e) => set(jf, e.target.value)}/></Field>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="admin-edit-cancel">Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => onSave(f)} data-testid="admin-edit-save">Save</Button>
        </div>
      </div>
    </div>
  );
}

const Field = ({label, children}) => <div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
const Stat = ({label, value, testId}) => (
  <div className="rle-card p-5" data-testid={testId}>
    <div className="rle-eyebrow">{label}</div><div className="mt-1 font-display font-bold text-2xl text-slate-900">{value}</div>
  </div>
);

function SeoEditor() {
  const [pages, setPages] = useState(null); const [editing, setEditing] = useState(null); const [gen, setGen] = useState(null);
  const load = () => api.get("/admin/seo/pages").then(({data}) => setPages(data.pages));
  useEffect(() => { api.get("/admin/seo/pages").then(({data}) => setPages(data.pages)); }, []);
  const regenerate = async (code) => {
    if (!window.confirm(`Regenerate the ${code} page with AI? This replaces the current copy.`)) return;
    setGen(code);
    try { await api.post(`/admin/seo/generate/${code}`); toast.success(`${code} page regenerated`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setGen(null); }
  };
  const save = async (f) => {
    const payload = { ...f };
    for (const jf of ["sections", "faqs"]) { if (typeof payload[jf] === "string") { try { payload[jf] = JSON.parse(payload[jf]); } catch { toast.error(`Invalid JSON in ${jf}`); return; } } }
    await api.post("/admin/seo/pages", payload); toast.success("Saved"); setEditing(null); load();
  };
  const missing = ["AU", "CA", "UK"].filter((c) => pages && !pages.some((p) => p.country === c));
  return (
    <div>
      <div><h1 className="font-display font-extrabold text-3xl text-slate-900">SEO Pages</h1><p className="text-slate-600 text-sm mt-1">Long-form "English for migrants" guides. Public at /english-for-migrants/&lt;country&gt;.</p></div>
      {!pages ? <div className="mt-6 text-slate-500">Loading…</div> : (
        <div className="mt-6 space-y-3">
          {pages.map((p) => (
            <div key={p.id} className="rle-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-seo-row-${p.country}`}>
              <div className="min-w-0">
                <div className="rle-eyebrow">{p.country} · {p.status} · {p.sections?.length || 0} sections · {p.faqs?.length || 0} FAQs</div>
                <div className="font-semibold text-slate-900 mt-1 truncate">{p.title}</div>
                <a href={`/english-for-migrants/${p.slug.replace("english-for-migrants-", "")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">/{p.slug.replace("english-for-migrants-", "english-for-migrants/")}</a>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`admin-seo-edit-${p.country}`}>Edit</Button>
                <Button size="sm" variant="outline" disabled={!!gen} onClick={() => regenerate(p.country)} data-testid={`admin-seo-regen-${p.country}`}>
                  {gen === p.country ? <Loader2 size={14} className="animate-spin mr-1"/> : <Sparkles size={14} className="mr-1"/>}Regenerate with AI
                </Button>
              </div>
            </div>
          ))}
          {missing.map((c) => (
            <div key={c} className="rle-card p-5 flex items-center justify-between gap-3 border-dashed" data-testid={`admin-seo-missing-${c}`}>
              <div className="text-slate-600 text-sm">{c} guide not generated yet (generation runs automatically on server start; you can trigger it now).</div>
              <Button size="sm" disabled={!!gen} onClick={() => regenerate(c)} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`admin-seo-generate-${c}`}>
                {gen === c ? <Loader2 size={14} className="animate-spin mr-1"/> : <Sparkles size={14} className="mr-1"/>}Generate
              </Button>
            </div>
          ))}
        </div>
      )}
      {editing && <SeoEditModal page={editing} onClose={() => setEditing(null)} onSave={save}/>}
    </div>
  );
}

function SeoEditModal({ page, onClose, onSave }) {
  const [f, setF] = useState(() => ({ ...page, sections: JSON.stringify(page.sections, null, 2), faqs: JSON.stringify(page.faqs, null, 2) }));
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-seo-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
        <h2 className="font-display font-bold text-xl text-slate-900">Edit {page.country} guide</h2>
        <div className="mt-4 space-y-3">
          <Field label={`SEO title (${(f.title || "").length}/60)`}><Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="admin-seo-title"/></Field>
          <Field label={`Meta description (${(f.meta_description || "").length}/155)`}><Textarea rows={2} value={f.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid="admin-seo-meta"/></Field>
          <Field label="H1"><Input value={f.h1 || ""} onChange={(e) => set("h1", e.target.value)} data-testid="admin-seo-h1"/></Field>
          <Field label="Status"><select value={f.status || "published"} onChange={(e) => set("status", e.target.value)} className="border rounded-lg px-2 py-2 w-full" data-testid="admin-seo-status"><option value="draft">draft</option><option value="published">published</option></select></Field>
          <Field label="Intro"><Textarea rows={5} value={f.intro || ""} onChange={(e) => set("intro", e.target.value)}/></Field>
          <Field label="Sections (JSON: heading, body)"><Textarea rows={14} className="font-mono text-xs" value={f.sections} onChange={(e) => set("sections", e.target.value)} data-testid="admin-seo-sections"/></Field>
          <Field label="FAQs (JSON: question, answer)"><Textarea rows={8} className="font-mono text-xs" value={f.faqs} onChange={(e) => set("faqs", e.target.value)}/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA heading"><Input value={f.cta_heading || ""} onChange={(e) => set("cta_heading", e.target.value)}/></Field>
            <Field label="CTA text"><Input value={f.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)}/></Field>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="admin-seo-cancel">Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => onSave(f)} data-testid="admin-seo-save">Save</Button>
        </div>
      </div>
    </div>
  );
}

function SettingsView() {
  const [data, setData] = useState(null); const [plans, setPlans] = useState(""); const [limits, setLimits] = useState("");
  const load = () => api.get("/admin/settings").then(({data}) => {
    setData(data); setPlans(JSON.stringify(data.plans, null, 2)); setLimits(JSON.stringify(data.usage_limits, null, 2));
  });
  useEffect(() => { load(); }, []);
  const savePlans = async () => { try { await api.post("/admin/settings/plans", JSON.parse(plans)); toast.success("Plans saved"); } catch (e) { toast.error("Invalid JSON"); } };
  const saveLimits = async () => { try { await api.post("/admin/settings/limits", JSON.parse(limits)); toast.success("Limits saved"); } catch (e) { toast.error("Invalid JSON"); } };
  if (!data) return <div className="text-slate-500">Loading…</div>;
  return (
    <div className="max-w-3xl">
      <h1 className="font-display font-extrabold text-3xl text-slate-900">Settings</h1>
      <div className="mt-6 rle-card p-6">
        <div className="font-semibold text-slate-900">Plans (pricing & features)</div>
        <Textarea rows={14} className="font-mono text-xs mt-2" value={plans} onChange={(e) => setPlans(e.target.value)} data-testid="admin-plans-json"/>
        <Button onClick={savePlans} className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-3" data-testid="admin-plans-save">Save plans</Button>
      </div>
      <div className="mt-6 rle-card p-6">
        <div className="font-semibold text-slate-900">Usage limits</div>
        <Textarea rows={10} className="font-mono text-xs mt-2" value={limits} onChange={(e) => setLimits(e.target.value)} data-testid="admin-limits-json"/>
        <Button onClick={saveLimits} className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-3" data-testid="admin-limits-save">Save limits</Button>
      </div>
    </div>
  );
}
